import React from 'react';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import {
  Box,
  List,
  Drawer,
  Divider,
  SvgIcon,
  Typography,
  ListItem,
  IconButton,
  ListItemIcon,
  ListItemText,
} from '@material-ui/core';
import { useFusionAuth } from '@fusionauth/react-sdk';

import i18n from 'i18n-js';
import { buildUrl, clearStorageItems } from 'utils';
import SettingsIcon from '@material-ui/icons/Settings';
import ExitToAppIcon from '@material-ui/icons/ExitToApp';
import ArrowBackIosIcon from '@material-ui/icons/ArrowBackIos';
import ArrowForwardIosIcon from '@material-ui/icons/ArrowForwardIos';
import Loading from 'components/Loading';
import styled from 'styled-components';
import routes from './routes';
import Can from 'components/Can';
import Feature from 'components/Feature';
import { NavLink } from 'react-router-dom';
import OrgAbility from 'components/OrgAbility';
import SharingAbility from '../SharingAbility';
import ReceivableAbility from 'components/ReceivableAbility';
import { ConfigRenderer } from '../ConfigRenderer';
import { AuthActions } from 'state/modules/auth';
import { useSelector } from 'react-redux';

import { useStyles, SidebarTooltip } from './sidebar.styled';
import { AccessControl } from '../AccessControl/AccessControl';

const NavWrapper = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column-reverse;
  div:empty + .sidebar__section-header {
    display: none;
  }
`;

/**
 * Validate users permissions
 * @param {object} route Contains all params to check users permissions
 * @param {node} children The element to be rendered
 */
const ValidatePermissions = ({ route, children }) => {
  if (route.privilege) {
    if (route.configuration) {
      return (
        <Feature
          name={route?.configuration?.name}
          is={route?.configuration?.is}
          not={route?.configuration?.not}
        >
          <Can I={route?.privilege?.do} on={route?.privilege?.on}>
            {children}
          </Can>
        </Feature>
      );
    } else {
      return (
        <Can I={route?.privilege?.do} on={route?.privilege?.on}>
          {children}
        </Can>
      );
    }
  } else if (route.configuration) {
    return (
      <Feature
        name={route?.configuration?.name}
        is={route?.configuration?.is}
        not={route?.configuration?.not}
      >
        {children}
      </Feature>
    );
  }

  return children;
};

const ValidateRole = ({ route, user, children }) => {
  if (!route.role || (route.role && route.role === user?.role)) {
    return children;
  }
  return null;
};

const Sidebar = ({ open, handleDrawerOpen }) => {
  const dispatch = useDispatch();
  const classes = useStyles();
  const { user } = useSelector((state) => state.auth);
  const { loading } = useSelector((state) => state.config?.settings || {});
  const receivableType = useSelector((state) => state.config?.settings?.core?.receivable?.type);
  const isLoggedInWithFusionAuth = useSelector((state) => state.auth.currentBastion !== null);

  const { startLogout } = useFusionAuth();

  const handleLogout = async () => {
    if (isLoggedInWithFusionAuth) {
      await dispatch(AuthActions.logout({ is_fusion_auth: true }));
      startLogout();
    } else {
      dispatch(AuthActions.logout());
    }
  };

  /**
   * Render link elemnt
   */
  const Element = ({ route }) => {
    return (
      <NavLink
        button="true"
        to={buildUrl(route.path)}
        className={clsx(classes.link, {
          [classes.linkClosed]: !open,
        })}
        activeClassName={classes.active}
        data-cy={route.path}
      >
        <SidebarTooltip
          placement="right"
          title={i18n.t(`sidebar.${route.label}`)}
          disableHoverListener={open ? true : false}
        >
          <ListItem button={true}>
            <ListItemIcon
              className={clsx(classes.linkIcon, {
                [classes.linkCollapse]: !open,
              })}
            >
              <SvgIcon className={classes.svgIcon} component={route.icon} />
            </ListItemIcon>
            <ListItemText className={classes.linkText} primary={i18n.t(`sidebar.${route.label}`)} />
          </ListItem>
        </SidebarTooltip>
      </NavLink>
    );
  };

  /**
   * Render link item according to privileges and configurations
   */
  const NavItem = ({ route }) => {
    return (
      <AccessControl ac={route.accessControl}>
        {/* TODO:
        Translate organizationType, user, sharingType, configRenderer
        to accessControl in order to delete
        OrgAbility/ValidateRole/ValidatePermissions/SharingAbility/ConfigRenderer
        */}
        <OrgAbility
          subject={
            Array.isArray(route.organizationType)
              ? route.organizationType
              : [route.organizationType]
          }
          allowed={route.organizationType ? false : true}
        >
          <ValidateRole route={route} user={user}>
            <ValidatePermissions route={route}>
              <SharingAbility
                subject={Array.isArray(route.sharingType) ? route.sharingType : [route.sharingType]}
                allowed={route.sharingType ? false : true}
              >
                <ReceivableAbility
                  subject={
                    Array.isArray(route.receivableType)
                      ? route.receivableType
                      : [route.receivableType]
                  }
                  allowed={
                    route.receivableType && (receivableType || route.receivableType === 'billing')
                      ? false
                      : true
                  }
                >
                  <ConfigRenderer
                    subject={route.configRenderer?.subject}
                    selector={route.configRenderer?.selector}
                  >
                    <Element route={route} />
                  </ConfigRenderer>
                </ReceivableAbility>
              </SharingAbility>
            </ValidatePermissions>
          </ValidateRole>
        </OrgAbility>
      </AccessControl>
    );
  };

  return (
    <Drawer
      variant="permanent"
      className={clsx(classes.drawer, {
        [classes.drawerOpen]: open,
        [classes.drawerClose]: !open,
      })}
      classes={{
        paper: clsx(classes.paper, {
          [classes.drawerOpen]: open,
          [classes.drawerClose]: !open,
        }),
      }}
    >
      <Box
        display="flex"
        flexDirection="column"
        style={{ height: '100%' }}
        justifyContent="space-between"
      >
        <Box
          height="100%"
          display="flex"
          flexDirection="column"
          justifyContent="space-between"
          className={clsx(classes.listContainer, {
            [classes.listContainerCollapsed]: !open,
          })}
        >
          <Box height="100%">
            {loading ? (
              <Loading />
            ) : (
              <List className={classes.list}>
                {routes.map((route, index) => {
                  if (route.children?.length > 0) {
                    return (
                      <NavWrapper key={index}>
                        <div>
                          {route.children.map((child) => (
                            <NavItem key={child.path + child.label} route={child} />
                          ))}
                        </div>
                        {route.title && (
                          <div className="sidebar__section-header">
                            <Divider
                              className={clsx(classes.divider, {
                                [classes.dividerCollapsed]: !open,
                              })}
                            />
                            {open ? (
                              <Typography variant="subtitle2" className={classes.sectionTitle}>
                                {i18n.t(`sidebar.sections.${route.title}`)}
                              </Typography>
                            ) : null}
                          </div>
                        )}
                      </NavWrapper>
                    );
                  }
                  return null;
                })}
              </List>
            )}
          </Box>
        </Box>
        <Divider className={classes.divider} />
        <Box>
          <Divider className={classes.divider} />
          <List className={classes.secondList}>
            <NavLink
              to={buildUrl('/settings')}
              button="true"
              className={clsx(classes.link, {
                [classes.linkClosed]: !open,
              })}
              style={{ display: 'none' }}
              activeClassName={classes.active}
            >
              <SidebarTooltip
                placement="right"
                title={i18n.t('sidebar.settings')}
                disableHoverListener={open ? true : false}
              >
                <ListItem button={true}>
                  <ListItemIcon
                    className={clsx(classes.linkIcon, {
                      [classes.linkCollapse]: !open,
                    })}
                  >
                    <SvgIcon component={SettingsIcon} />
                  </ListItemIcon>
                  <ListItemText className={classes.linkText} primary={i18n.t('sidebar.settings')} />
                </ListItem>
              </SidebarTooltip>
            </NavLink>
            <SidebarTooltip
              placement="right"
              title={i18n.t('sidebar.logout')}
              disableHoverListener={open ? true : false}
            >
              <ListItem
                data-cy="logout-btn"
                button={true}
                className={classes.link}
                style={{ paddingLeft: 24 }}
                onClick={handleLogout}
              >
                <ListItemIcon
                  className={clsx(classes.linkIcon, {
                    [classes.linkCollapse]: !open,
                  })}
                >
                  <SvgIcon component={ExitToAppIcon} />
                </ListItemIcon>
                <ListItemText className={classes.linkText} primary={i18n.t('sidebar.logout')} />
              </ListItem>
            </SidebarTooltip>
          </List>
        </Box>
        <Box
          py={1}
          display="flex"
          alignItems="center"
          justifyContent={open ? 'flex-end' : 'center'}
        >
          <IconButton
            aria-label="open drawer"
            onClick={handleDrawerOpen}
            className={clsx(classes.menu)}
          >
            {open ? <ArrowBackIosIcon /> : <ArrowForwardIosIcon />}
          </IconButton>
        </Box>
      </Box>
    </Drawer>
  );
};

Sidebar.propTypes = {
  open: PropTypes.bool,
};

Sidebar.defaultProps = {
  open: false,
};

export default Sidebar;
